import { useCallback, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import Auth from "../../Auth";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../AppEventPublisher";

export type SourceTable = "characters" | "worldbuilding_entries" | "outlines";

export type Snapshot = {
  id: string;
  sourceTable: SourceTable;
  // The shape varies by sourceTable; keep as any for now
  data: any;
};

type UseElementHistoryControllerProps = {
  elementId: string;
  sourceTable: SourceTable;
  projectId: string;
  restore?: (snapshot: Snapshot) => Promise<void> | void;
  listLabel?: string;
};

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onRestoreItem: () => Promise<void>;
  loading: boolean;
  restoring: boolean;
  isCreatingSnapshot: boolean;
  listLabel: string;
};

type UseElementHistoryControllerReturn = {
  isOpen: boolean;
  open: () => Promise<void>;
  close: () => void;
  loading: boolean;
  restoring: boolean;
  isCreatingSnapshot: boolean;
  selectedSnapshot: Snapshot | null;
  setSelectedSnapshot: React.Dispatch<React.SetStateAction<Snapshot | null>>;
  fetchHistory: (limit?: number, offset?: number) => Promise<Snapshot[]>;
  handleRestore: () => Promise<void>;
  modalProps: ModalProps;
};

/**
 * Controller hook to manage Element History modal state and data fetching.
 *
 * Usage:
 * const history = useElementHistoryController({ elementId, sourceTable, projectId, restore, listLabel })
 * history.open() // opens modal and creates a snapshot
 * <HistoryModal isOpen={history.isOpen} onClose={history.close} fetchHistory={history.fetchHistory} ... />
 */
export default function useElementHistoryController({
  elementId,
  sourceTable,
  projectId,
  restore,
  listLabel = "History",
}: UseElementHistoryControllerProps): UseElementHistoryControllerReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);

  const createHistoricalRecord = useCallback(async () => {
    if (!elementId || !sourceTable) return;
    try {
      setIsCreatingSnapshot(true);
      if (sourceTable === "characters") {
        await Auth.fetchWithIdToken(`/api/characters/${elementId}/snapshot`, {
          method: "POST",
          body: JSON.stringify({ projectId }),
        });
      } else if (sourceTable === "worldbuilding_entries") {
        await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${elementId}/snapshot`, {
          method: "POST",
          body: JSON.stringify({ projectId }),
        });
      } else if (sourceTable === "outlines") {
        await Auth.fetchWithIdToken(`/api/outline/${elementId}/snapshot`, {
          method: "POST",
          body: JSON.stringify({ projectId }),
        });
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { elementId, sourceTable, projectId },
      });
    } finally {
      setIsCreatingSnapshot(false);
    }
  }, [elementId, sourceTable, projectId]);

  const fetchHistory = useCallback(
    async (limit = 50, offset = 0): Promise<Snapshot[]> => {
      if (!elementId || !sourceTable) return [];
      try {
        setLoading(true);
        if (sourceTable === "characters") {
          const response: any = await Auth.fetchWithIdToken(`/api/characters/${elementId}/history`, {
            method: "POST",
            limit,
            offset,
          });
          return (response?.history as Snapshot[]) || [];
        }
        if (sourceTable === "worldbuilding_entries") {
          const response: any = await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${elementId}/history`, {
            method: "POST",
            limit,
            offset,
          });
          return (response?.history as Snapshot[]) || [];
        }
        if (sourceTable === "outlines") {
          const response: any = await Auth.fetchWithIdToken(`/api/outline/${elementId}/history`, {
            method: "POST",
            projectId,
            limit,
            offset,
          });
          return (response?.history as Snapshot[]) || [];
        }
        return [];
      } catch (error) {
        Sentry.captureException(error, {
          tags: { elementId, sourceTable, projectId },
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [elementId, sourceTable, projectId]
  );

  const open = useCallback(async () => {
    setIsOpen(true);
    const sourceTableMap = {
      characters: StoryBibleType.characters,
      worldbuilding_entries: StoryBibleType.worldbuilding,
      outlines: StoryBibleType.outline,
    };
    AppEventPublisher.storyBible.action(sourceTableMap[sourceTable], StoryBibleAction.OpenHistory);
    await createHistoricalRecord();
  }, [createHistoricalRecord]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleRestore = useCallback(async () => {
    if (!restore || !selectedSnapshot) return;
    setRestoring(true);
    await restore(selectedSnapshot);
    setRestoring(false);
    setIsOpen(false);
  }, [restore, selectedSnapshot]);

  const modalProps = useMemo<ModalProps>(
    () => ({
      isOpen,
      onClose: close,
      onRestoreItem: handleRestore,
      loading,
      restoring,
      isCreatingSnapshot,
      listLabel,
    }),
    [isOpen, close, handleRestore, loading, restoring, isCreatingSnapshot, listLabel]
  );

  return {
    isOpen,
    open,
    close,
    loading,
    restoring,
    isCreatingSnapshot,
    selectedSnapshot,
    setSelectedSnapshot,
    fetchHistory,
    handleRestore,
    modalProps,
  };
}
